import { RetrievalMethod } from '@/feature/profiles/constants';
import {
  COMPLETE_MEDICAL_RECORDS,
  formDefaultValues,
} from '@/feature/patients/constants';
import { toIsoDateString } from '@/utils/date';
import type {
  PatientCreationState,
  ProviderFacilityService,
  RequestDetails,
  FacesheetService,
  RecordUploadService,
} from '@/feature/patients/types';
import type { ProviderFacilityFormData } from '@/feature/patients/components/request-steps/request/suggested-providers-facility/validation';
import type { Facesheet } from '@/feature/profiles/types';
import type { PresignedUrlResponse } from '@/types/file-upload';

/**
 * Maps a FacesheetService (from API response) to a partial Facesheet object
 * that can be used by FacesheetSelector for preselection.
 * The selector matches by facesheetConfigId, so we map the API's `id` to `facesheetConfigId`.
 */
const mapFacesheetServiceToFacesheet = (
  facesheetService: FacesheetService,
): Facesheet => ({
  facesheetConfigId: facesheetService.id,
  organizationId: '',
  displayName: facesheetService.name,
  templateTypes: [],
  isExpedited: facesheetService.expedited,
});

/**
 * Maps a RecordUploadService (from API response) to a partial PresignedUrlResponse object
 * for display in edit mode. The file is already uploaded on the server,
 * so we only need the filename for display purposes.
 */
const mapRecordUploadServiceToFile = (
  recordUpload: RecordUploadService,
): PresignedUrlResponse => ({
  id: '',
  filename: recordUpload.fileName,
  metadata: {},
  signedUrl: '',
  createdAt: '',
  expiresAt: '',
});

export const mapProviderFacilityServiceToFormData = ({
  id,
  providerName,
  facilityName,

  specialty,
  address,
  phoneNumber,
  faxNumber,
  recordsRequested,
  serviceStartDate,
  serviceEndDate,
}: ProviderFacilityService): ProviderFacilityFormData & { id?: number } => {
  const startDate = toIsoDateString(serviceStartDate);
  const endDate = toIsoDateString(serviceEndDate);
  // backend return recordsRequested:'Complete Medical Records' as complete records, and any other string as specific records
  const isSpecific = recordsRequested !== COMPLETE_MEDICAL_RECORDS;
  const normalizedRecordsRequested = isSpecific
    ? ('specific' as const)
    : ('complete' as const);

  return {
    id,
    providerName: (providerName || '').trim(),
    facilityName: (facilityName || '').trim(),
    specialty: (specialty || '').trim(),
    address1: (address?.address1 || '').trim(),
    address2: (address?.address2 || '').trim(),
    city: (address?.city || '').trim(),
    state: (address?.state || '').trim(),
    postalCode: (address?.postalCode || '').trim(),
    phoneNumber: (phoneNumber || '').trim(),
    faxNumber: (faxNumber || '').trim(),
    recordsRequested: normalizedRecordsRequested,
    serviceDateRange:
      startDate && endDate ? ('custom' as const) : ('6months' as const),
    serviceStartDate: startDate || '',
    serviceEndDate: endDate || '',
    medicalRecordDetails: isSpecific ? recordsRequested : '',
  };
};

// this is to determine the request methods on an existing request
export const mapPatientRequestDetailsToState = (
  details: RequestDetails,
  defaults: PatientCreationState = formDefaultValues,
): PatientCreationState => {
  const selectedRequestMethods: RetrievalMethod[] = [];

  if (details.retrieveFrom?.length) {
    selectedRequestMethods.push(RetrievalMethod.PROVIDER_FACILITY);
  }
  if (details.hie?.length) {
    selectedRequestMethods.push(RetrievalMethod.HIE);
  }
  if (details.medications) {
    selectedRequestMethods.push(RetrievalMethod.MEDICATIONS);
  }
  if (details.recordUpload) {
    selectedRequestMethods.push(RetrievalMethod.RECORD_UPLOAD);
  }
  if (details.facesheets?.length) {
    selectedRequestMethods.push(RetrievalMethod.FACESHEETS);
  }

  const providerFacilityRetrieveFrom =
    details.retrieveFrom?.map(mapProviderFacilityServiceToFormData) || [];

  // Map facesheets from API response to Facesheet objects for preselection
  const selectedFacesheets =
    details.facesheets?.map(mapFacesheetServiceToFacesheet) || [];

  // Map record upload from API response for display in edit mode
  const uploadedFiles = details.recordUpload
    ? [mapRecordUploadServiceToFile(details.recordUpload)]
    : [];

  // Map release step data
  // If previousDocumentIds is empty, preselect "I do not have release authorization forms"
  // Otherwise, preselect the documents from previousDocumentIds
  const hasPreviousDocuments =
    details.previousDocumentIds && details.previousDocumentIds.length > 0;

  const nextState: PatientCreationState = {
    ...defaults,
    request: {
      ...defaults.request,
      selectedRequestMethods,
      actionFormsData: {
        ...defaults.request.actionFormsData,
        [RetrievalMethod.HIE]: {
          alternativePostalCodes:
            details.hie?.flatMap((h) => h.alternativePostalCodes ?? []) ?? [],
        },
        [RetrievalMethod.MEDICATIONS]: {
          retrieveMedications: !!details.medications,
        },
        [RetrievalMethod.PROVIDER_FACILITY]: {
          requestFrom: details.requestFrom || '',
          releasedTo: details.releasedTo || '',
          retrieveFrom: providerFacilityRetrieveFrom,
        },
        [RetrievalMethod.FACESHEETS]: {
          selectedFacesheets,
        },
        [RetrievalMethod.RECORD_UPLOAD]: {
          uploadedFiles,
        },
      },
    },
    release: {
      ...defaults.release,
      selectedPreviousDocuments: details.previousDocumentIds || [],
      noReleaseAuthorizationForms: !hasPreviousDocuments,
    },
    submit: {
      ...defaults.submit,
      notificationEmails: details.notificationEmails || [],
      referenceId: details.referenceId || undefined,
      requestReason: details.dateNeededBy
        ? 'upcoming-appointment'
        : 'information-gathering',
      dateNeededBy: toIsoDateString(details.dateNeededBy),
    },
  };

  return nextState;
};
